import type { ReactNode } from "react";
import type { ResizeHandle as ResizeHandleAxis } from "react-resizable";

import * as React from "react";

import { PortalContainerProvider } from "@replohq/lattice/components/portal-theme/PortalTheme";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { GripHorizontal, Minus, X } from "lucide-react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import * as z from "zod";

// ---------------------------------------------------------------------------
// Resize handles
// ---------------------------------------------------------------------------

const RESIZE_HANDLES: ResizeHandleAxis[] = [
  "se",
  "sw",
  "ne",
  "nw",
  "s",
  "e",
  "w",
  "n",
];

const HANDLE_CURSOR: Record<ResizeHandleAxis, string> = {
  n: "cursor-ns-resize",
  s: "cursor-ns-resize",
  e: "cursor-ew-resize",
  w: "cursor-ew-resize",
  ne: "cursor-nesw-resize",
  sw: "cursor-nesw-resize",
  nw: "cursor-nwse-resize",
  se: "cursor-nwse-resize",
};

const HANDLE_CSS_CURSOR: Record<ResizeHandleAxis, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
};

const HANDLE_POSITION: Record<ResizeHandleAxis, string> = {
  n: "left-0 top-0 h-1.5 w-full",
  s: "bottom-0 left-0 h-1.5 w-full",
  e: "right-0 top-0 h-full w-1.5",
  w: "left-0 top-0 h-full w-1.5",
  ne: "right-0 top-0 h-3 w-3",
  nw: "left-0 top-0 h-3 w-3",
  se: "bottom-0 right-0 h-3 w-3",
  sw: "bottom-0 left-0 h-3 w-3",
};

const FloatingResizeHandle = React.forwardRef<
  HTMLDivElement,
  { axis: ResizeHandleAxis } & React.HTMLAttributes<HTMLDivElement>
>(({ axis, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute z-10 hover:bg-blue-300/40",
      HANDLE_CURSOR[axis],
      HANDLE_POSITION[axis],
    )}
    {...props}
  />
));
FloatingResizeHandle.displayName = "FloatingResizeHandle";

// ---------------------------------------------------------------------------
// Deterministic header coloring — all panels on the same port share a color,
// different ports get different colors.
// ---------------------------------------------------------------------------

const HEADER_COLORS = [
  "bg-blue-900 border-blue-800 hover:bg-blue-800",
  "bg-emerald-900 border-emerald-800 hover:bg-emerald-800",
  "bg-purple-900 border-purple-800 hover:bg-purple-800",
  "bg-amber-900 border-amber-800 hover:bg-amber-800",
  "bg-rose-900 border-rose-800 hover:bg-rose-800",
  "bg-cyan-900 border-cyan-800 hover:bg-cyan-800",
  "bg-indigo-900 border-indigo-800 hover:bg-indigo-800",
  "bg-teal-900 border-teal-800 hover:bg-teal-800",
] as const;

function getHeaderColor(): string {
  const port = typeof window !== "undefined" ? window.location.port : "";
  let hash = 0;
  for (const char of port) {
    hash = (hash * 31 + char.charCodeAt(0)) | 0;
  }
  return HEADER_COLORS[Math.abs(hash) % HEADER_COLORS.length]!;
}

// ---------------------------------------------------------------------------
// Minimized bar tiling registry
// ---------------------------------------------------------------------------

const MINIMIZED_BAR_WIDTH = 280;
const MINIMIZED_BAR_GAP = 8;
const MINIMIZED_BAR_LEFT_OFFSET = 16;

const minimizedPanels = new Set<string>();
const minimizedListeners = new Set<() => void>();
let minimizedSnapshot: readonly string[] = [];

function emitMinimizedChange() {
  minimizedSnapshot = [...minimizedPanels].sort();
  for (const listener of minimizedListeners) {
    listener();
  }
}

function subscribeMinimized(listener: () => void): () => void {
  minimizedListeners.add(listener);
  return () => minimizedListeners.delete(listener);
}

function getMinimizedSnapshot(): readonly string[] {
  return minimizedSnapshot;
}

function registerMinimized(key: string) {
  minimizedPanels.add(key);
  emitMinimizedChange();
}

function unregisterMinimized(key: string) {
  minimizedPanels.delete(key);
  emitMinimizedChange();
}

function useMinimizedIndex(storageKey: string, isMinimized: boolean): number {
  const sorted = React.useSyncExternalStore(
    subscribeMinimized,
    getMinimizedSnapshot,
    getMinimizedSnapshot,
  );

  // eslint-disable-next-line replo/no-use-effect -- Synchronizes mounted panels with the minimized-panel registry.
  React.useEffect(() => {
    if (isMinimized) {
      registerMinimized(storageKey);
      return () => unregisterMinimized(storageKey);
    }
  }, [storageKey, isMinimized]);

  return sorted.indexOf(storageKey);
}

// ---------------------------------------------------------------------------
// Geometry persistence
// ---------------------------------------------------------------------------

const panelGeometrySchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

type PanelGeometry = z.infer<typeof panelGeometrySchema>;

// ---------------------------------------------------------------------------
// Viewport clamping
// ---------------------------------------------------------------------------

interface ViewportSize {
  width: number;
  height: number;
}

const EMPTY_VIEWPORT_SIZE: ViewportSize = { width: 0, height: 0 };
const PANEL_VIEWPORT_GUTTER = 16;
let viewportSnapshot = EMPTY_VIEWPORT_SIZE;

function getViewportSnapshot(): ViewportSize {
  if (typeof window === "undefined") {
    return EMPTY_VIEWPORT_SIZE;
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  if (viewportSnapshot.width !== width || viewportSnapshot.height !== height) {
    viewportSnapshot = { width, height };
  }
  return viewportSnapshot;
}

function subscribeToViewport(listener: () => void): () => void {
  window.addEventListener("resize", listener);
  return () => window.removeEventListener("resize", listener);
}

function useViewportSize(): ViewportSize {
  return React.useSyncExternalStore(
    subscribeToViewport,
    getViewportSnapshot,
    () => EMPTY_VIEWPORT_SIZE,
  );
}

function clampGeometry(
  geometry: PanelGeometry,
  viewport: ViewportSize,
): PanelGeometry {
  if (viewport.width === 0 || viewport.height === 0) {
    return geometry;
  }

  const availableWidth = Math.max(
    0,
    viewport.width - PANEL_VIEWPORT_GUTTER * 2,
  );
  const availableHeight = Math.max(
    0,
    viewport.height - PANEL_VIEWPORT_GUTTER * 2,
  );
  const width = Math.min(geometry.width, availableWidth);
  const height = Math.min(geometry.height, availableHeight);

  return {
    width,
    height,
    x: Math.max(
      PANEL_VIEWPORT_GUTTER,
      Math.min(geometry.x, viewport.width - width - PANEL_VIEWPORT_GUTTER),
    ),
    y: Math.max(
      PANEL_VIEWPORT_GUTTER,
      Math.min(geometry.y, viewport.height - height - PANEL_VIEWPORT_GUTTER),
    ),
  };
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FloatingPanelProps extends React.PropsWithChildren {
  title: string;
  storageKey: string;
  defaultSize: { width: number; height: number };
  defaultPosition?: "center" | "bottom-left";
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  onClose: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
  headerExtra?: ReactNode;
}

function getDefaultPosition(
  size: { width: number; height: number },
  position: "center" | "bottom-left",
  viewport: ViewportSize,
) {
  if (viewport.width === 0 || viewport.height === 0) {
    return { x: 100, y: 100 };
  }
  if (position === "bottom-left") {
    return {
      x: MINIMIZED_BAR_LEFT_OFFSET,
      y: viewport.height - size.height - MINIMIZED_BAR_LEFT_OFFSET,
    };
  }
  return {
    x: Math.round((viewport.width - size.width) / 2),
    y: Math.round((viewport.height - size.height) / 2),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function FloatingPanelHeader({
  title,
  headerColor,
  headerExtra,
  onClose,
  onMinimize,
  draggable,
}: {
  title: string;
  headerColor: string;
  headerExtra?: ReactNode;
  onClose: () => void;
  onMinimize?: () => void;
  draggable: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-8 shrink-0 items-center justify-between border-b px-3",
        draggable
          ? "floating-panel-drag-handle cursor-grab active:cursor-grabbing"
          : null,
        headerColor,
      )}
      onDoubleClick={() => onMinimize?.()}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {draggable && (
          <GripHorizontal
            className="size-3 shrink-0 text-slate-500" /* eslint-disable-line replo/no-tailwind-literal-color -- illustration surface uses a fixed palette outside shared product tokens. */
          />
        )}
        <span className="select-none truncate text-xs font-medium text-white">
          {title}
        </span>
      </div>
      {/* NOTE (Ezra, 2026-07-28): no-drag + z-20 so minimize/close taps aren't swallowed by drag or corner resize handles. */}
      <div className="floating-panel-no-drag relative z-20 flex shrink-0 items-center">
        {headerExtra}
        {onMinimize && (
          <button
            type="button"
            aria-label="Minimize"
            className="flex size-8 items-center justify-center rounded text-slate-400 hover:bg-white/10 hover:text-white" /* eslint-disable-line replo/no-tailwind-literal-color -- illustration surface uses a fixed palette outside shared product tokens. */
            onClick={onMinimize}
          >
            <Minus className="size-3.5" />
          </button>
        )}
        <button
          type="button"
          aria-label="Close"
          className="flex size-8 items-center justify-center rounded text-slate-400 hover:bg-white/10 hover:text-white" /* eslint-disable-line replo/no-tailwind-literal-color -- illustration surface uses a fixed palette outside shared product tokens. */
          onClick={onClose}
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

const FloatingPanel: React.FC<FloatingPanelProps> = ({
  title,
  storageKey,
  defaultSize,
  defaultPosition = "center",
  minSize = { width: 320, height: 200 },
  maxSize = { width: 2000, height: 1200 },
  onClose,
  onMinimize,
  isMinimized,
  children,
  headerExtra,
}) => {
  const isMobile = useIsMobile();
  const viewport = useViewportSize();
  const defaultPos = getDefaultPosition(defaultSize, defaultPosition, viewport);
  const [geometry, setGeometry] = useLocalStorageState(
    storageKey,
    { ...defaultPos, ...defaultSize },
    { schema: panelGeometrySchema },
  );
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const headerColor = React.useMemo(() => getHeaderColor(), []);
  const minimizedIndex = useMinimizedIndex(storageKey, Boolean(isMinimized));
  const [interactionCursor, setInteractionCursor] = React.useState<
    string | null
  >(null);
  // Portalled popups default to document.body's z-150 layer, below this panel's z-[10000], so they'd open behind it; mount them into a higher-z layer instead.
  const [overlayRoot, setOverlayRoot] = React.useState<HTMLDivElement | null>(
    null,
  );

  const { x, y, width, height } = clampGeometry(
    geometry ?? { ...defaultPos, ...defaultSize },
    viewport,
  );
  const availableWidth = Math.max(
    0,
    viewport.width - PANEL_VIEWPORT_GUTTER * 2,
  );
  const availableHeight = Math.max(
    0,
    viewport.height - PANEL_VIEWPORT_GUTTER * 2,
  );
  const maxWidth = viewport.width
    ? Math.min(maxSize.width, availableWidth)
    : maxSize.width;
  const maxHeight = viewport.height
    ? Math.min(maxSize.height, availableHeight)
    : maxSize.height;
  const minWidth = Math.min(minSize.width, maxWidth);
  const minHeight = Math.min(minSize.height, maxHeight);

  if (isMinimized) {
    const left =
      minimizedIndex >= 0
        ? MINIMIZED_BAR_LEFT_OFFSET +
          minimizedIndex * (MINIMIZED_BAR_WIDTH + MINIMIZED_BAR_GAP)
        : MINIMIZED_BAR_LEFT_OFFSET;

    return (
      <div
        className={cn(
          "fixed bottom-0 z-[10000] flex h-8 cursor-pointer items-center justify-between gap-3 rounded-t-md border border-b-0 px-3 shadow-lg",
          headerColor,
        )}
        style={
          isMobile
            ? { left: 0, right: 0, width: "100%" }
            : { left, width: MINIMIZED_BAR_WIDTH }
        }
        onClick={() => onMinimize?.()}
      >
        <span className="text-xs font-medium text-white">{title}</span>
        <div className="flex items-center gap-1.5">
          {headerExtra}
          <button
            type="button"
            aria-label="Close"
            className="flex size-8 items-center justify-center text-slate-400 hover:text-white" /* eslint-disable-line replo/no-tailwind-literal-color -- illustration surface uses a fixed palette outside shared product tokens. */
            onClick={(event) => {
              event.stopPropagation();
              onClose();
            }}
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Phones can't usefully drag/resize a floating card — dock as a scrollable
  // bottom sheet instead so the debugger stays fully reachable.
  if (isMobile) {
    return (
      <>
        <div className="fixed inset-x-0 bottom-0 z-[10000] flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-xl border border-border bg-sheet text-default shadow-lg">
          <FloatingPanelHeader
            title={title}
            headerColor={headerColor}
            headerExtra={headerExtra}
            onClose={onClose}
            onMinimize={onMinimize}
            draggable={false}
          />
          <div className="flex min-h-0 flex-1 flex-col overflow-auto">
            <PortalContainerProvider container={overlayRoot}>
              {children}
            </PortalContainerProvider>
          </div>
        </div>
        {/* Zero-size, transform-free layer above the panel for portalled popups. */}
        <div ref={setOverlayRoot} className="fixed left-0 top-0 z-[10050]" />
      </>
    );
  }

  return (
    <>
      {interactionCursor && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{ cursor: interactionCursor }}
        />
      )}
      <Draggable
        handle=".floating-panel-drag-handle"
        // NOTE (Ezra, 2026-07-28): Exclude header controls so the rest of the
        // bar stays draggable while mobile taps on minimize/close still click.
        cancel=".floating-panel-no-drag"
        nodeRef={nodeRef}
        position={{ x, y }}
        onStart={() => {
          setInteractionCursor("grabbing");
        }}
        onStop={(_e, data) => {
          setInteractionCursor(null);
          setGeometry((previousGeometry) => {
            return clampGeometry(
              {
                ...(previousGeometry ?? { ...defaultPos, ...defaultSize }),
                x: data.x,
                y: data.y,
              },
              viewport,
            );
          });
        }}
        bounds="body"
      >
        <div
          ref={nodeRef}
          className="fixed z-[10000] overflow-hidden rounded-xl"
          style={{ top: 0, left: 0, width, height }}
        >
          <Resizable
            width={width}
            height={height}
            minConstraints={[minWidth, minHeight]}
            maxConstraints={[maxWidth, maxHeight]}
            resizeHandles={RESIZE_HANDLES}
            handle={(
              axis: ResizeHandleAxis,
              ref?: React.Ref<HTMLDivElement>,
            ) => <FloatingResizeHandle ref={ref} axis={axis} />}
            onResizeStart={(_e, { handle }) => {
              setInteractionCursor(HANDLE_CSS_CURSOR[handle]);
            }}
            onResize={(_e, { size, handle }) => {
              setGeometry((previousGeometry) => {
                const geometry = clampGeometry(
                  previousGeometry ?? {
                    ...defaultPos,
                    ...defaultSize,
                  },
                  viewport,
                );
                const deltaWidth = size.width - geometry.width;
                const deltaHeight = size.height - geometry.height;
                return clampGeometry(
                  {
                    width: size.width,
                    height: size.height,
                    x: handle.includes("w")
                      ? geometry.x - deltaWidth
                      : geometry.x,
                    y: handle.includes("n")
                      ? geometry.y - deltaHeight
                      : geometry.y,
                  },
                  viewport,
                );
              });
            }}
            onResizeStop={() => {
              setInteractionCursor(null);
            }}
          >
            <div
              className="flex flex-col overflow-hidden rounded-xl border border-border bg-sheet text-default shadow-lg"
              style={{ width, height }}
            >
              <FloatingPanelHeader
                title={title}
                headerColor={headerColor}
                headerExtra={headerExtra}
                onClose={onClose}
                onMinimize={onMinimize}
                draggable
              />
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <PortalContainerProvider container={overlayRoot}>
                  {children}
                </PortalContainerProvider>
              </div>
            </div>
          </Resizable>
        </div>
      </Draggable>
      {/* Zero-size, transform-free layer above the panel for portalled popups. */}
      <div ref={setOverlayRoot} className="fixed left-0 top-0 z-[10050]" />
    </>
  );
};

export { FloatingPanel };
